import { useDashboardData } from "../../hooks/useDashboardData";
import Verified from "./components/Verified";
import { IdCard, Loader2, ShieldAlert } from "lucide-react";
import { KycVerification } from "../kyc/KycVerification";
import { useMemo, useState } from "react";
import { KYC_STATUS } from "../../constants/kyc";
import KYCGuy from "../../assets/KYCGuyBG.png";
import { useWallet } from "@solana/wallet-adapter-react";
import { useAuth } from "../../contexts/AuthContext";
import { AuthenticationFlow } from "../../components/auth/AuthenticationFlow";
import canaryImg from "../../assets/canary.png";

export function Dashboard() {
  const wallet = useWallet();
  const { isAuthenticated } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const { data, error, startPolling, pollingLoading, pollingError } =
    useDashboardData();
  // Main content - wrapped in useMemo to avoid unnecessary re-renders
  const mainContent = useMemo(() => {
    return (
      <div className="flex items-center justify-center">
        <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-8 mb-12">
            <div className="md:w-2/5">
              <h1 className="text-5xl font-black mb-6 text-zinc-900 tracking-tight font-heading">
                TIME TO
                <br />
                VERIFY!
              </h1>
              <p className="text-base text-zinc-700 mb-6 font-body">
                Degen Coin Flip is licensed and regulated by the Government of
                Anjouan. If you've reached <strong>85 SOL</strong> in gameplay
                on wallet (
                <strong>
                  {wallet.publicKey?.toBase58().slice(0, 4)}..
                  {wallet?.publicKey?.toBase58().slice(-4)}
                </strong>
                ), we need to verify your wallet.
              </p>
              <div className="mb-6 font-body">
                <p className="flex items-center gap-2 mb-2">
                  <span className="text-green-500 text-xl">✓</span>
                  <span>Take a photo of government ID</span>
                </p>
                <p className="flex items-center gap-2">
                  <span className="text-green-500 text-xl">✓</span>
                  <span>Provide full legal name</span>
                </p>
              </div>
              <div className="mb-8">
                {!wallet.connected || !isAuthenticated ? (
                  <AuthenticationFlow />
                ) : (
                  <KycVerification
                    startPolling={startPolling}
                    pollingLoading={pollingLoading}
                    pollingError={pollingError}
                  />
                )}
              </div>
            </div>
            <div className="md:w-3/5">
              <img
                src={KYCGuy}
                alt="KYC Verification"
                className="w-full rounded-3xl shadow-lg"
              />
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold mb-6 text-center font-body">
              Frequently Asked Question
            </h2>
            <div className="space-y-4">
              {[
                {
                  question: "WHAT IF I'M FROM A RESTRICTED COUNTRY?",
                  answer: (
                    <>
                      You will not be able to go through the KYC process if you
                      are from the following countries: United States, France,
                      Australia, Netherlands, Curacao, Austria, Belize, Comoros,
                      Germany, Spain, Statia, St. Martin, United Kingdom,
                      Countries blacklisted by the Financial Action Task Force
                      (FATF).
                      <br />
                      <br />
                      <span className="text-red-500">
                        Important: Our team cannot assist with verification for
                        restricted regions. Users found to be from restricted
                        countries will be automatically blocked to comply with
                        regulations.
                      </span>
                    </>
                  ),
                },
                {
                  question: "HOW LONG DOES IT TAKE?",
                  answer: "Most users take ~ 2 minutes to complete.",
                },
                {
                  question: "WHY DO I NEED TO VERIFY MY IDENTITY?",
                  answer: (
                    <>
                      Degen Coin Flip is licensed and regulated by the
                      Government of the Anjouan and if you've reached{" "}
                      <strong>85 SOL</strong> in game play, we are required to
                      verify wallet address.
                    </>
                  ),
                },
                {
                  question: "WHAT DO I NEED?",
                  answer:
                    "You'll need a valid government-issued photo ID (such as a passport, driver's license, or national ID card), email, and an address.",
                },
                {
                  question: "IS MY DATA SAFE?",
                  answer: (
                    <>
                      Yes. Your personal data is stored on secure servers in the
                      EU (AWS/Azure) using AES-256 encryption, compliant with
                      ISO 27001 and GDPR standard.
                      <br />
                      <div className="mt-3 p-3 bg-blue-50 rounded border border-blue-100">
                        <p className="text-sm text-blue-800">
                          <span className="font-medium">Privacy Note:</span>{" "}
                          Your wallet addresses are encrypted and obfuscated on
                          our servers. We do not directly link wallets to
                          verification data unless legally required by valid
                          court order - nowhere else.
                        </p>
                      </div>
                      <div className="mt-4 flex items-center gap-3 text-sm flex-wrap min-w-0 overflow-hidden">
                        <img
                          src={canaryImg}
                          alt="Cartoon Canary"
                          style={{
                            maxWidth: 36,
                            width: 36,
                            height: 36,
                            boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
                            borderRadius: 8,
                            background: "white",
                            padding: 2,
                            flexShrink: 0,
                          }}
                        />
                        <svg
                          className="w-4 h-4 text-green-500 flex-shrink-0"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span
                          className="underline text-primary-600 whitespace-normal break-words min-w-0"
                          style={{
                            overflowWrap: "break-word",
                            wordBreak: "break-word",
                            flex: 1,
                          }}
                        >
                          Last transparency check — 0 secret data requests
                          (PGP-signed 2026-03-24).{" "}
                          <a
                            href="/canary-archive"
                            className="hover:text-primary-700"
                          >
                            [Full report▸]
                          </a>
                        </span>
                      </div>
                    </>
                  ),
                },
                {
                  question: "WHAT IF I HAVE MORE THAN ONE WALLET?",
                  answer:
                    "Once verified, you will be able to link your other wallets through a personal link.",
                },
              ].map((faq, index) => (
                <details
                  key={index}
                  className="group bg-white rounded-3xl shadow-sm"
                  open
                >
                  <summary className="flex justify-between items-center cursor-pointer p-6">
                    <span className="font-medium text-start text-zinc-500 font-heading">
                      {faq.question}
                    </span>
                    <span className="ml-6 flex-shrink-0 text-zinc-400 group-open:rotate-180 transition-transform duration-200">
                      ▼
                    </span>
                  </summary>
                  <div className="px-6 pb-6 text-zinc-600 text-start font-body">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }, [
    startPolling,
    pollingLoading,
    pollingError,
    wallet.connected,
    isAuthenticated,
    wallet.publicKey,
  ]);

  // Review needed or declined state
  const retryContent = useMemo(() => {
    const reviewStatuses: string[] = [
      KYC_STATUS.NEEDS_REVIEW,
      KYC_STATUS.DECLINED,
    ];
    if (!data || !data.status || !reviewStatuses.includes(data.status))
      return null;

    const isDeclined = data.status === KYC_STATUS.DECLINED;

    return (
      <div className="flex items-center justify-center mt-md-6">
        <div className="w-full max-w-xl mx-auto bg-white rounded-lg shadow-sm p-8">
          <div className="text-center mb-8">
            {isDeclined ? (
              <ShieldAlert className="mx-auto w-16 h-16 text-red-700 mb-6" />
            ) : (
              <IdCard className="mx-auto w-16 h-16 text-blue-700 mb-6" />
            )}
            <h2
              className={`text-2xl font-semibold mb-6 ${
                isDeclined ? "text-red-700" : "text-blue-700"
              }`}
            >
              {isDeclined
                ? "Your verification was declined"
                : "Sorry, we were unable to verify your ID"}
            </h2>

            <div className="text-left mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {isDeclined
                  ? "Reason for decline:"
                  : "We detected the following issues:"}
              </h3>

              <div className="flex items-start space-x-3 p-4 bg-red-50 rounded-lg">
                <div className="flex-shrink-0">
                  <svg
                    className="w-5 h-5 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <p className="text-gray-700">{data.importantReason}</p>
              </div>
            </div>

            <div className="text-left space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Please follow these steps:
                </h3>
                <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
                  <li>
                    Ensure all four corners of your ID are clearly visible
                  </li>
                  <li>Make sure there's good lighting with no glare</li>
                  <li>
                    Take a photo of the original document, not a screen or copy
                  </li>
                  <li>Keep your ID flat against a dark background</li>
                </ul>
              </div>

              <div className="text-center">
                {!wallet.connected || !isAuthenticated ? (
                  <AuthenticationFlow />
                ) : (
                  <KycVerification
                    startPolling={() => {
                      setIsProcessing(true);
                      return Promise.resolve();
                    }}
                    pollingLoading={pollingLoading}
                    pollingError={pollingError}
                  />
                )}
              </div>

              <div className="text-sm text-gray-500 mt-6">
                <p>
                  Need help?{" "}
                  <a
                    href="https://discord.com/invite/rDmmvuqg3A"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    Reach out to our support team on Discord
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }, [
    data,
    pollingLoading,
    pollingError,
    wallet.connected,
    isAuthenticated,
    setIsProcessing,
  ]);

  const processingContent = useMemo(() => {
    return (
      <div className="flex items-center justify-center mt-md-6">
        <div className="w-full max-w-xl mx-auto bg-white rounded-lg shadow-sm p-8 text-center">
          <Loader2 className="mx-auto w-16 h-16 text-blue-700 mb-6 animate-spin" />
          <h2 className="text-2xl font-semibold mb-4 text-gray-900">
            Processing Your Application
          </h2>
          <p className="text-gray-600 mb-6">
            Please close this window and check back in a few moments.
          </p>
          <div className="text-sm text-gray-500">
            <p>
              Need help?{" "}
              <a
                href="https://discord.com/invite/rDmmvuqg3A"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline"
              >
                Reach out to our support team on Discord
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }, []);

  if (error) {
    return mainContent;
  }

  // Show processing content if status is PROCESSING
  if (isProcessing) {
    return processingContent;
  }

  // Show retry content if status is NEEDS_REVIEW or DECLINED
  if (
    data?.status === KYC_STATUS.NEEDS_REVIEW ||
    data?.status === KYC_STATUS.DECLINED
  ) {
    return retryContent;
  }

  // Show authentication flow if not authenticated or no data
  if (!wallet.connected || !isAuthenticated || !data) {
    return mainContent;
  }

  // Success state (COMPLETED)
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {data.status === KYC_STATUS.COMPLETED && <Verified />}
    </div>
  );
}
